import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CTASection } from "@/components/CTASection";
import { LinkedText } from "@/components/LinkedText";
import { PhoneFrame } from "@/components/PhoneFrame";
import { VIDEOS_HUB, WATCH_PAGES } from "@/data/watchPages";

/** Hub card design switch: "phone-card" = whole card is a phone, "phone-thumb" = small phone around the thumbnail. */
const CARD_STYLE: "phone-card" | "phone-thumb" = "phone-card";

type Page = (typeof WATCH_PAGES)[number];

function PhoneCard({ p }: { p: Page }) {
  return (
    <article className="phone-frame-metal relative mx-auto flex h-full w-full max-w-[300px] rounded-[2.75rem] p-[10px]">
      <span aria-hidden className="phone-frame-metal-button absolute left-[-3px] top-[90px] h-8 w-[4px] rounded-l" />
      <span aria-hidden className="phone-frame-metal-button absolute left-[-3px] top-[136px] h-12 w-[4px] rounded-l" />
      <span aria-hidden className="phone-frame-metal-button absolute left-[-3px] top-[196px] h-12 w-[4px] rounded-l" />
      <span aria-hidden className="phone-frame-metal-button absolute right-[-3px] top-[150px] h-16 w-[4px] rounded-r" />
      <div className="phone-frame-metal-screen flex min-h-[540px] w-full flex-col rounded-[2.2rem] px-4 pb-6 pt-6">
        <div className="relative mx-auto aspect-[4/5] w-full overflow-hidden rounded-xl bg-black">
          <Link to={`/videos/${p.slug}`} tabIndex={-1} className="block h-full w-full">
            <img
              src={`https://img.youtube.com/vi/${p.videoId}/hqdefault.jpg`}
              alt={p.h1}
              width={480}
              height={360}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 h-full w-full scale-[1.45] object-cover object-center"
            />
          </Link>
        </div>
        <h3 className="mt-5 text-lg font-bold mb-2 text-foreground">{p.h1}</h3>
        <p className="text-muted-foreground"><LinkedText text={p.card} /></p>
      </div>
    </article>
  );
}

export default function VideosHub() {
  return (
    <>
      <Helmet>
        <title>{VIDEOS_HUB.title}</title>
        <meta name="description" content={VIDEOS_HUB.description} />
        <meta property="og:title" content={VIDEOS_HUB.title} />
        <meta property="og:description" content={VIDEOS_HUB.description} />
        <link rel="canonical" href="https://masonry-pro.com/videos" />
      </Helmet>
      <Header />
      <nav aria-label="Breadcrumb" className="bg-primary border-b border-primary/80 pt-28 md:pt-32">
        <div className="container mx-auto px-4">
          <ol className="flex items-center gap-2 py-4 text-base">
            <li>
              <Link to="/" className="flex items-center gap-1 text-primary-foreground/70 hover:text-secondary transition-colors">
                <Home className="h-5 w-5" />
                <span className="sr-only">Home</span>
              </Link>
            </li>
            <li className="flex items-center gap-2">
              <ChevronRight className="h-5 w-5 text-primary-foreground/40" />
              <span className="text-secondary font-semibold">Project Videos</span>
            </li>
          </ol>
        </div>
      </nav>
      <main className="bg-background">
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">{VIDEOS_HUB.h1}</h1>
              <p className="text-lg text-muted-foreground">{VIDEOS_HUB.intro}</p>
            </div>
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4 max-w-7xl mx-auto">
              {WATCH_PAGES.map((p) => CARD_STYLE === "phone-card" ? (
                <PhoneCard key={p.slug} p={p} />
              ) : (
                <article key={p.slug} className="rounded-lg border border-border bg-card overflow-hidden">
                  <div className="p-6">
                    <PhoneFrame metal compact className="max-w-[160px]">
                  <Link to={`/videos/${p.slug}`} tabIndex={-1} className="block h-full w-full">
                    <img
                      src={`https://img.youtube.com/vi/${p.videoId}/hqdefault.jpg`}
                      alt={p.h1}
                      width={480}
                      height={360}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  </Link>
                    </PhoneFrame>
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-bold mb-2">{p.h1}</h3>
                    <p className="text-muted-foreground"><LinkedText text={p.card} /></p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
        <CTASection />
      </main>
      <Footer />
    </>
  );
}
